import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useIsAuthenticated } from "../../shared/auth/cognito-react";
import { useTheme, useMediaQuery } from "@mui/material";
import { Paper, Typography } from "@mui/material";
import MonetizationOnTwoToneIcon from "@mui/icons-material/MonetizationOnTwoTone";
import ArticleTwoToneIcon from "@mui/icons-material/ArticleTwoTone";
import ManageAccountsTwoToneIcon from "@mui/icons-material/ManageAccountsTwoTone";
import HistoryEduTwoToneIcon from "@mui/icons-material/HistoryEduTwoTone";
import TaxInsuranceDetails from "./components/TaxInsuranceDetails";
import DashboardPaymentActivity from "./components/DashboardPaymentActivity";
import DashboardPaymentDetails from "./components/DashboardPaymentDetails";

import DashboardSummaryCard from "./components/DashboardSummaryCard";
// import NavbarAccountInfo from "../../shared/components/NavbarAccountInfo";
import { ThemeContext } from "../../contexts/ThemeContext";
import { ConfiguratorContext } from "../../contexts/ConfiguratorContext";
import { AuthContext } from "../../contexts/AuthContext";
import { getPaymentActivity } from "../../data/paymentsAPI";
import { PageTitle } from "../../shared/components/styled/Typography.styled";
import {
  DashboardCardMedium,
  DashboardCardSmall,
  FlexContainer,
} from "../../shared/components/styled/Container.styled";
import ConfirmationDialog from "../../shared/components/ConfirmationDialog";
import ActionSnackbar from "../../shared/components/ActionSnackbar";
import DescriptionSection from "../../shared/components/dashboard/DescriptionSection";
import {
  ERROR,
  NOT_APPLICABLE,
  PLACEHOLDER_DESCRIPTION,
  SUCCESS,
} from "../../shared/constants/Defaults.constants";
import {
  PAYMENT_ACTIVITY_FIELD_TYPES,
  PAYMENT_ACTIVITY_HEADER_NAMES,
  PAYMENT_ACTIVITY_NO_DATA_MESSAGE,
} from "../../shared/constants/Payments.constants";
import RequireAuth from "../../shared/components/guards/RequireAuth";
import {
  findElementIncludedInArray,
  hex2rgba,
  toDateStringNoDay,
} from "../../shared/utils/utility-functions";
import {
  BANNER_IMAGE_DASHBOARD_KEY,
  BANNER_URL_DASHBOARD_KEY,
  DASHBOARD_PAYMENT_ACTIVITY_CAPTION_KEY,
  DASHBOARD_TAX_AND_INSURANCE_CAPTION_KEY,
  ESTATEMENT_SIGNUP_TERMS_AND_CONDITIONS_KEY,
  INACTIVE_LOAN_DASHBOARD_MESSAGE_KEY,
  INACTIVE_LOAN_DASHBOARD_ALLOW_KEY,
} from "../../shared/constants/Configurator.constants";
import MarketingBanner from "../../shared/components/configurator/MarketingBanner";
import { format } from "date-fns";
import {
  NO_LOAN_REGISTERED_WARNING_MESSAGE,
  PAPER_STATEMENT_UPDATE_PREFERENCES_FAILED,
  PAPER_STATEMENT_UPDATE_PREFERENCES_SUCCESS,
} from "../../shared/constants/UserProfile.constants";
import { utcToZonedTime } from "date-fns-tz";
import {
  CLIENT_ADMIN_ROLE,
  PAYMENTS_BORROWER_ROLE,
  PAYMENTS_SUPPORT_ROLE,
} from "../../shared/constants/Roles.constants";
import { getLoanData, updatePrefereneces } from "../../data/loanAPI";
import EditableTermsAndConditions from "../../shared/components/configurator/EditableTermsAndConditions";
import LoanDetailsExpandable from "../../shared/components/LoanDetailsExpandable";
import { useResponsivePadding } from "../../shared/hooks/useResponsivePadding";
import { useTotalAmountDueConfig } from "../../shared/hooks/useTotalAmountDueConfig";
import PageLayout from "../../shared/components/PageLayout";
// import LoanDetails from "./components/LoanDetails";
// import LoanBalance from "./components/LoanBalance";
import { formatCurrency } from "../../shared/utils/currency";
import { isLoanClosedToPayments } from "../../shared/utils/payment-functions";

const Dashboard = () => {
  const { addPendingRequest, removePendingRequest } = useContext(ThemeContext);
  const { edittedFields } = useContext(ConfiguratorContext);
  const {
    token,
    userDetails,
    userRoles,
    activeLoan,
    activeLoanDetails,
    setActiveLoanDetails,
    impersonatedUser,
    impersonatedUserDetails,
  } = useContext(AuthContext);
  const isAuthenticated = useIsAuthenticated();
  const navigate = useNavigate();
  const theme = useTheme();
  const isLessThanMd = useMediaQuery(theme.breakpoints.down("md"));
  // const isLessThanLg = useMediaQuery(theme.breakpoints.down("lg"));
  const { mainContainer, bannerContainer } = useResponsivePadding();
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const inActiveOrDeadLoan =
    activeLoanDetails?.loanDetails?.inActiveIndicator ||
    activeLoanDetails?.loanDetails?.suspendedIndicator ||
    activeLoanDetails?.loanDetails?.deadLoanIndicator;
  const loanClosedToPayments = isLoanClosedToPayments(
    activeLoanDetails?.loanDetails,
  );
  const hasNoRegisteredLoan = activeLoan === "no-loan";
  const { showTotalAmountDue } = useTotalAmountDueConfig();

  const handleLoanWidgetClick = (event) => {
    if (hasNoRegisteredLoan) {
      event?.preventDefault?.();
      return;
    }
    setLoanOpen(true);
  };

  const handleStatementsDocumentsWidgetClick = (event) => {
    if (hasNoRegisteredLoan) {
      event?.preventDefault?.();
      return;
    }
    navigate("/statements-documents");
  };

  // Payment Details Card States
  const [dueDate, setDueDate] = useState(new Date().toLocaleDateString());
  const [autoPaymentDate, setAutoPaymentDate] = useState(
    new Date().toLocaleDateString(),
  );
  const [lastPaymentReceived, setLastPaymentDate] = useState(
    new Date().toLocaleDateString(),
  );
  const [amountDue, setAmountDue] = useState("0");
  const [totalAmountDue, setTotalAmountDue] = useState("0");
  const [principalAndInterest, setPrincipalAndInterest] = useState("0");
  const [charges, setCharges] = useState(NOT_APPLICABLE);
  const [escrow, setEscrow] = useState("0");
  const [autopay, setAutopay] = useState(false);

  // Summary Card States
  const [loanAmount, setLoanAmount] = useState("0");
  const [, setNewStatementsAndDocuments] = useState(0);
  const [estatementDialogOpen, setEstatementDialogOpen] = useState(false);

  // Tax And Insurance States
  const [realEstateCurrent, setRealEstateCurrent] = useState(NOT_APPLICABLE);
  const [hazardCurrent, setHazardCurrent] = useState(NOT_APPLICABLE);

  // Payment Activity States
  const [paymentActivity, setPaymentActivity] = useState([]);

  // Configurator States
  const [taxAndInsuranceDescription, setTaxAndInsuranceDescription] = useState(
    PLACEHOLDER_DESCRIPTION,
  );
  const [paymentActivityDescription, setPaymentActivityDescription] = useState(
    PLACEHOLDER_DESCRIPTION,
  );

  // Snackbar States
  // Loan
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  // E-Statement
  const [severity, setSeverity] = useState("error");
  const [snackbarEStatementOpen, setSnackbarEStatementOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [paperless, setPaperless] = useState(false);

  //Loan Pop-up
  const [loanOpen, setLoanOpen] = useState(false);

  //Terms and Conditions E-Statement
  const [termsAndConditionsText, setTermsAndConditionsText] = useState("");
  const [termsAndConditionsCheckbox, setTermsAndConditionsCheckbox] =
    useState(false);

  useEffect(() => {
    if (userRoles?.includes(PAYMENTS_SUPPORT_ROLE) && !impersonatedUser) {
      navigate("/support", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAuthenticated && userDetails && token) {
      callDashboardApi(token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetails, isAuthenticated, token, activeLoan]);

  useEffect(() => {
    setPaperless(activeLoanDetails?.loanDetails?.paperless);
  }, [activeLoanDetails]);

  useEffect(() => {
    fillConfiguratorFields(edittedFields);
  }, [edittedFields]);

  const callDashboardApi = async (token) => {
    addPendingRequest();
    if (activeLoan) {
      if (activeLoan !== "no-loan") {
        let loanDetails = activeLoanDetails;
        if (
          !loanDetails ||
          activeLoan !== loanDetails?.loanDetails?.loanId
        ) {
          loanDetails = await getLoanData(
            token,
            activeLoan,
            impersonatedUser,
          );
          if (loanDetails?.loanDetails?.loanId !== activeLoan) {
            removePendingRequest();
            return;
          }
          setActiveLoanDetails(loanDetails);
        }

        const sumFormatter = new Intl.NumberFormat("en-US");
        setDashboardDataStates(loanDetails, sumFormatter);
        const paymentActivityData = await getPaymentActivity(
          token,
          impersonatedUser,
          activeLoan,
          1,
          4,
        );
        setPaymentActivityDataStates(paymentActivityData, sumFormatter);
      } else {
        setSnackbarOpen(true);
      }
    }
    removePendingRequest();
  };

  const setDashboardDataStates = (data, sumFormatter) => {
    setDueDate(data.loanDetails.dueDate.replaceAll("-", "/"));
    setAutoPaymentDate(
      data.loanDetails.autoPaymentDate
        ? format(
            utcToZonedTime(data.loanDetails.autoPaymentDate, userTimeZone),
            "MM/dd/yyyy",
          )
        : "",
    );
    setLastPaymentDate(
      data.loanDetails.lastPaymentReceived.replaceAll("-", "/"),
    );
    setAmountDue(formatCurrency(+data.loanDetails.amountDue));
    // Shown verbatim when the client has turned the question on: the core system owns the
    // calculation, so nothing here floors, clamps or falls back to the amount due (US-986802).
    setTotalAmountDue(formatCurrency(+data.loanDetails.totalAmountDue));
    setPrincipalAndInterest(
      formatCurrency(+data.loanDetails.amountPrincipalInterest),
    );
    setCharges(
      data?.loanDetails?.charges
        ? formatCurrency(+data.loanDetails.charges)
        : NOT_APPLICABLE,
    );
    setEscrow(
      data?.loanDetails?.currentEscrowPayment
        ? formatCurrency(+data.loanDetails.currentEscrowPayment)
        : NOT_APPLICABLE,
    );
    setAutopay(data.loanDetails.autoPayActive);
    setLoanAmount(
      formatCurrency(+data.loanDetails.mortgageBalance),
    );
    setNewStatementsAndDocuments(
      data.documents && data.statements ? data.statements + data.documents : 0,
    );
    setRealEstateCurrent(
      data.realEstateTax
        ? formatCurrency(+data.realEstateTax)
        : NOT_APPLICABLE,
    );
    const currentHazard = data.insuranceDetails.find(
      (item) => item?.type.toString().toLowerCase() === "hazard",
    );
    setHazardCurrent(
      currentHazard
        ? formatCurrency(+currentHazard.amount)
        : NOT_APPLICABLE,
    );
  };

  const setPaymentActivityDataStates = (data, sumFormatter) => {
    if (!data || data.message || !Array.isArray(data.response)) {
      setPaymentActivity([]);
      return;
    }
    const tableData = data.response
      .filter((item) => item?.status && "PENDING" !== item.status.toUpperCase())
      .map((item) => {
        const itemDate = utcToZonedTime(item.date, userTimeZone);
        const formattedDate = `${`0${itemDate.getMonth() + 1}`.slice(
          -2,
        )}/${`0${itemDate.getDate()}`.slice(-2)}/${itemDate.getFullYear()}`;
        return {
          type: item.paymentType,
          date: item.date ? formattedDate : "Unknown Date",
          status: item.status.toUpperCase(),
          amount: formatCurrency(item.amount),
          disabled: item.status.toUpperCase() === "PENDING",
        };
      });
    setPaymentActivity(tableData.slice(0, 4));
  };

  const fillConfiguratorFields = (fields) => {
    fields.forEach((item) => {
      switch (item.key) {
        case DASHBOARD_TAX_AND_INSURANCE_CAPTION_KEY:
          setTaxAndInsuranceDescription(item.value);
          return;
        case DASHBOARD_PAYMENT_ACTIVITY_CAPTION_KEY:
          setPaymentActivityDescription(item.value);
          return;
        case ESTATEMENT_SIGNUP_TERMS_AND_CONDITIONS_KEY:
          setTermsAndConditionsText(item.value);
          return;
        default:
          return;
      }
    });
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setSnackbarOpen(false);
  };

  const confirmOptinEStatement = async () => {
    setEstatementDialogOpen(false);
    let status;
    if (termsAndConditionsCheckbox) {
      addPendingRequest();
      if (activeLoan) {
        if (activeLoan !== "no-loan") {
          status = await updatePrefereneces(
            token,
            activeLoan,
            impersonatedUser,
            true,
          );
          const updatedLoanDetails = await getLoanData(
            token,
            activeLoan,
            impersonatedUser,
          );
          if (updatedLoanDetails?.loanDetails?.loanId === activeLoan) {
            setActiveLoanDetails(updatedLoanDetails);
            setPaperless(updatedLoanDetails.loanDetails.paperless);
          }
        }
      }
      removePendingRequest();
      displayStatusAPICall(status);
    }
  };

  const displayStatusAPICall = (status) => {
    if (status && status.success) {
      setSnackbarMessage(PAPER_STATEMENT_UPDATE_PREFERENCES_SUCCESS);
      setSeverity(SUCCESS);
    } else {
      setSnackbarMessage(PAPER_STATEMENT_UPDATE_PREFERENCES_FAILED);
      setSeverity(ERROR);
    }
    setSnackbarEStatementOpen(true);
  };

  return (
    <RequireAuth>
      {!isLessThanMd && (
        <PageTitle
          variant="h1"
          component="h1"
          color="primary.dark"
          fontSize="3rem"
          display='inline-block'
        >
          Welcome,{" "}
          {impersonatedUserDetails
            ? impersonatedUserDetails?.displayName
            : userDetails?.name}
          !
        </PageTitle>
      )}
      
      {inActiveOrDeadLoan ? (
        <Typography
          fontSize="1rem"
          fontWeight="bold"
          color="warning.main"
          display='inline-block'
          padding="35px"
        >
          {(edittedFields.find((item) => item.key === INACTIVE_LOAN_DASHBOARD_ALLOW_KEY)?.value || 'false') === 'true' ?
            (edittedFields.find((item) => item.key === INACTIVE_LOAN_DASHBOARD_MESSAGE_KEY)?.value || "") : ""}
        </Typography>
      ) : ""}

      {/* No Paper wrapper here: the top tiles must sit directly on the admin
          "Background color" (theme background.default). The tiles keep the
          admin "Foreground color" via DashboardCardMedium/Small. */}
      {isLessThanMd ? (
        <PageLayout>
          <FlexContainer flexdirection="column" width="100%">
          <DashboardCardMedium>
            <DashboardPaymentDetails
              dueDate={toDateStringNoDay(dueDate)}
              autopay={autopay}
              amountDue={amountDue}
              totalAmountDue={totalAmountDue}
              showTotalAmountDue={showTotalAmountDue}
              principalInterestAmount={principalAndInterest}
              chargesAmount={charges}
              escrowAmount={escrow}
              lastPaymentReceived={toDateStringNoDay(lastPaymentReceived)}
              autoPaymentDate={autoPaymentDate}
            />
          </DashboardCardMedium>
        </FlexContainer>
        
          <FlexContainer 
            flexdirection="column" 
            width="100%"
            sx={{
              background: theme.palette.background.paper,
              borderRadius: "8px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
            }}
          >
            <DashboardSummaryCard
              Icon={ManageAccountsTwoToneIcon}
              iconColor="primary"
              label="Manage Your Account"
              breakpoint=" "
              data=" "
              valueFontSize="1.2857rem"
              clickAction={(e) => navigate("/profile")}
              cursor="pointer"
            />
            <DashboardSummaryCard
              Icon={ArticleTwoToneIcon}
              iconColor="primary"
              label="Statements & Documents"
              breakpoint=" "
              data=" "
              valueFontSize="1.2857rem"
              background={
                hasNoRegisteredLoan ? "rgba(0, 0, 0, 0.1)" : "transparent"
              }
              clickAction={handleStatementsDocumentsWidgetClick}
              cursor={hasNoRegisteredLoan ? "default" : "pointer"}
            />
            <DashboardSummaryCard
              Icon={MonetizationOnTwoToneIcon}
              iconColor="primary"
              label="Loan"
              breakpoint="."
              data={loanAmount}
              prefix="$"
              valueFontSize="1.2857rem"
              prefixFontSize="0.8571rem"
              background={
                hasNoRegisteredLoan ? "rgba(0, 0, 0, 0.1)" : "transparent"
              }
              clickAction={handleLoanWidgetClick}
              cursor={hasNoRegisteredLoan ? "default" : "pointer"}
            />
            {!inActiveOrDeadLoan ? (
              <DashboardSummaryCard
                background={
                  hasNoRegisteredLoan
                    ? "rgba(0, 0, 0, 0.1)"
                    : paperless && !userRoles?.includes(CLIENT_ADMIN_ROLE)
                      ? hex2rgba(theme.palette.primary.main, 0.1)
                      : "transparent"
                }
                Icon={HistoryEduTwoToneIcon}
                iconColor="primary"
                label="E-Statement Signup"
                breakpoint=" "
                data=" "
                valueFontSize="1.2857rem"
                clickAction={
                  (paperless && !userRoles?.includes(CLIENT_ADMIN_ROLE)) ||
                    hasNoRegisteredLoan
                    ? (event) => event.preventDefault()
                    : () => setEstatementDialogOpen(true)
                }
                cursor={
                  (paperless && !userRoles?.includes(CLIENT_ADMIN_ROLE)) ||
                    hasNoRegisteredLoan
                    ? "default"
                    : "pointer"
                }
              />
            ) : ""}
          </FlexContainer>
        </PageLayout>
      ) : (
        <FlexContainer
          padding={mainContainer}
          gap="25px"
          alignitems="center"
          width="100%"
        >
          <FlexContainer width="50%" sx={{height: {xs: 'auto', md: 'stretch'}}}>
            <DashboardCardMedium>
              <DashboardPaymentDetails
                dueDate={toDateStringNoDay(dueDate)}
                autopay={autopay}
                amountDue={amountDue}
                totalAmountDue={totalAmountDue}
                showTotalAmountDue={showTotalAmountDue}
                principalInterestAmount={principalAndInterest}
                chargesAmount={charges}
                escrowAmount={escrow}
                lastPaymentReceived={toDateStringNoDay(lastPaymentReceived)}
                autoPaymentDate={autoPaymentDate}
              />
            </DashboardCardMedium>
          </FlexContainer>
            <FlexContainer flexdirection="column" gap="25px" width="25%" height="stretch" justifycontent="space-between">
              <DashboardCardSmall sx={{aspectRatio: {xs: 'unset', md: '1/1', lg: 'unset'}}}>
                <DashboardSummaryCard
                  Icon={ManageAccountsTwoToneIcon}
                  iconColor="primary"
                  iconSize="64px"
                  label="Manage Your Account"
                  breakpoint=" "
                  data=" "
                  valueFontSize="1.2857rem"
                  clickAction={(e) => navigate("/profile")}
                  cursor="pointer"
                />
              </DashboardCardSmall>
              <DashboardCardSmall sx={{aspectRatio: {xs: 'unset', md: '1/1', lg: 'unset'}}}>
                <DashboardSummaryCard
                  Icon={ArticleTwoToneIcon}
                  iconColor="primary"
                  iconSize="64px"
                  label="Statements & Documents"
                  breakpoint=" "
                  data=" "
                  valueFontSize="1.2857rem"
                  background={
                    hasNoRegisteredLoan ? "rgba(0, 0, 0, 0.1)" : "transparent"
                  }
                  clickAction={handleStatementsDocumentsWidgetClick}
                  cursor={hasNoRegisteredLoan ? "default" : "pointer"}
                />
              </DashboardCardSmall>
            </FlexContainer>
            <FlexContainer flexdirection="column" gap="25px" width="25%" height="stretch" justifycontent="space-between">
              <DashboardCardSmall sx={{aspectRatio: {xs: 'unset', md: '1/1', lg: 'unset'}}}>
                <DashboardSummaryCard
                  Icon={MonetizationOnTwoToneIcon}
                  iconColor="primary"
                  iconSize="64px"
                  label="Loan"
                  breakpoint="."
                  data={loanAmount}
                  prefix="$"
                  valueFontSize="1.2857rem"
                  prefixFontSize="0.8571rem"
                  background={
                    hasNoRegisteredLoan ? "rgba(0, 0, 0, 0.1)" : "transparent"
                  }
                  clickAction={handleLoanWidgetClick}
                  cursor={hasNoRegisteredLoan ? "default" : "pointer"}
                />
              </DashboardCardSmall>
              <DashboardCardSmall sx={{aspectRatio: {xs: 'unset', md: '1/1', lg: 'unset'}}}>
                {!inActiveOrDeadLoan ? (
                  <DashboardSummaryCard
                    background={
                      hasNoRegisteredLoan
                        ? "rgba(0, 0, 0, 0.1)"
                        : paperless && !userRoles?.includes(CLIENT_ADMIN_ROLE)
                          ? hex2rgba(theme.palette.primary.main, 0.1)
                          : "transparent"
                    }
                    Icon={HistoryEduTwoToneIcon}
                    iconColor="primary"
                    iconSize="64px"
                    label="E-Statement Signup"
                    breakpoint=" "
                    data=" "
                    valueFontSize="1.2857rem"
                    clickAction={
                      (paperless && !userRoles?.includes(CLIENT_ADMIN_ROLE)) ||
                        hasNoRegisteredLoan
                        ? (event) => event.preventDefault()
                        : () => setEstatementDialogOpen(true)
                    }
                    cursor={
                      (paperless && !userRoles?.includes(CLIENT_ADMIN_ROLE)) ||
                        hasNoRegisteredLoan
                        ? "default"
                        : "pointer"
                    }
                  />
                ) : ""}
              </DashboardCardSmall>
            </FlexContainer>
        </FlexContainer>
        )}

        <FlexContainer padding={bannerContainer} alignitems="flex-start" width="100%">
          <MarketingBanner
            elementKey={BANNER_IMAGE_DASHBOARD_KEY}
            hyperlinkElementKey={BANNER_URL_DASHBOARD_KEY}
          />
        </FlexContainer>
      <Paper elevation={0}>
        <FlexContainer
          flexdirection="column"
          alignitems="flex-start"
          padding={mainContainer}
          gap={isLessThanMd ? "15px" : "25px"}
        >
          {/* <FlexContainer gap="25px" width="100%" justifycontent="flex-start">
            <LoanDetails />
            <LoanBalance />
          </FlexContainer> */}
          {activeLoanDetails?.loanDetails?.showEscrowTaxes ||
          activeLoanDetails?.loanDetails?.showEscrowInsurance ? (
            <FlexContainer gap={isLessThanMd ? "15px" : "25px"} width="100%" flexdirection={isLessThanMd ? "column" : "row"}>
              <DescriptionSection
                title="Tax & Insurance"
                body={taxAndInsuranceDescription}
                setBody={setTaxAndInsuranceDescription}
                bodyKey={DASHBOARD_TAX_AND_INSURANCE_CAPTION_KEY}
                wasModified={findElementIncludedInArray(
                  edittedFields,
                  "modified",
                  true,
                  "key",
                  DASHBOARD_TAX_AND_INSURANCE_CAPTION_KEY,
                )}
                // Has action disabled for now as no need in current scope
                hasAction={false}
                actionLabel="Find Insurance Plans"
                actionCallback={navigate}
                actionCallbackArguments={["/house"]}
                width={isLessThanMd ? "100%" : "30%"}
                inActiveOrDeadLoan={false}
              />
              <TaxInsuranceDetails
                realEstateCurrent={realEstateCurrent}
                hazardCurrent={hazardCurrent}
                insuranceDetails={activeLoanDetails?.insuranceDetails || []}
                showTaxes={!!activeLoanDetails?.loanDetails?.showEscrowTaxes}
                showInsurance={
                  !!activeLoanDetails?.loanDetails?.showEscrowInsurance
                }
              />
            </FlexContainer>
          ) : null}
          <FlexContainer gap={isLessThanMd ? "15px" : "25px"} width="100%" flexdirection={isLessThanMd ? "column" : "row"}>
            <DescriptionSection
              title="Payment Activity"
              body={paymentActivityDescription}
              setBody={setPaymentActivityDescription}
              bodyKey={DASHBOARD_PAYMENT_ACTIVITY_CAPTION_KEY}
              wasModified={findElementIncludedInArray(
                edittedFields,
                "modified",
                true,
                "key",
                DASHBOARD_PAYMENT_ACTIVITY_CAPTION_KEY,
              )}
              hasAction
              actionLabel="View Transaction History"
              actionCallback={navigate}
              actionCallbackArguments={["/payments/activity"]}
              width={isLessThanMd ? "100%" : "30%"}
              inActiveOrDeadLoan={loanClosedToPayments}
            />
            <DashboardPaymentActivity
              headers={PAYMENT_ACTIVITY_HEADER_NAMES}
              rows={paymentActivity}
              rowAttributes={PAYMENT_ACTIVITY_FIELD_TYPES}
              emptyDataMessage={PAYMENT_ACTIVITY_NO_DATA_MESSAGE}
              inActiveOrDeadLoan={loanClosedToPayments}
            />
          </FlexContainer>
        </FlexContainer>
      </Paper>
      {/*Pop-up dialog for E-Statement*/}
      <ConfirmationDialog
        open={estatementDialogOpen}
        dismiss={() => setEstatementDialogOpen(false)}
        closeButtonDisabled={!termsAndConditionsCheckbox}
        close={confirmOptinEStatement}
        title="Do you want to opt-in for e-statements?"
        body={
          <EditableTermsAndConditions
            width={"80%"}
            renderedText={termsAndConditionsText}
            setRenderedText={setTermsAndConditionsText}
            checkboxActive={termsAndConditionsCheckbox}
            setCheckboxActive={setTermsAndConditionsCheckbox}
            checkboxLabel="Yes"
            elementKey={ESTATEMENT_SIGNUP_TERMS_AND_CONDITIONS_KEY}
            wasModified={findElementIncludedInArray(
              edittedFields,
              "modified",
              true,
              "key",
              ESTATEMENT_SIGNUP_TERMS_AND_CONDITIONS_KEY,
            )}
            padding="8px 0"
            fontSize={12}
          />
        }
        hasActions={
          !!(!impersonatedUser && userRoles?.includes(PAYMENTS_BORROWER_ROLE))
        }
        confirmButtonLabel="Accept"
        dismissButtonLabel="Cancel"
      />
      {/*Loan pop-up dialog */}
      <ConfirmationDialog
        open={loanOpen}
        hasActions={false}
        hasTitle={false}
        hasComponent
        close={() => setLoanOpen(false)}
        dismiss={() => setLoanOpen(false)}
        body={<LoanDetailsExpandable position={"relative"} expanded popup />}
      />
      {/*Loan Message Snackbar*/}
      <ActionSnackbar
        severity="warning"
        borderColor={theme.palette.warning.main}
        vertical="top"
        horizontal="center"
        width="50%"
        isOpen={snackbarOpen}
        closeFn={handleSnackbarClose}
        snackbarLabel={NO_LOAN_REGISTERED_WARNING_MESSAGE}
        actionLabel="Register Loan"
        actionFn={() => navigate("/profile/loan")}
      />
      {/*E-Statement Snackbar*/}
      <ActionSnackbar
        severity={severity}
        borderColor={theme.palette[severity].main}
        vertical="top"
        horizontal="center"
        width={{ xs: "90%", md: "50%" }}
        isOpen={snackbarEStatementOpen}
        closeFn={() => {
          setSnackbarEStatementOpen(false);
        }}
        snackbarLabel={snackbarMessage}
        hasAction={false}
        autoHideDuration={6000}
      />
    </RequireAuth>
  );
};

export default Dashboard;